import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faXmark,
  faFileArrowDown,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "../../../Styles/components/file-upload.scss";

function formatFileSize(size) {
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getAllowedExtensions(accept) {
  return String(accept || "")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter((item) => item.startsWith("."));
}

function matchesAccept(file, accept) {
  if (!file || !accept) return true;

  const acceptedTypes = String(accept)
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  if (!acceptedTypes.length) return true;

  const fileName = String(file.name || "").toLowerCase();
  const mimeType = String(file.type || "").toLowerCase();

  return acceptedTypes.some((type) => {
    if (type.startsWith(".")) return fileName.endsWith(type);
    if (type.endsWith("/*")) {
      const baseType = type.replace("/*", "");
      return mimeType.startsWith(`${baseType}/`);
    }
    return mimeType === type;
  });
}

function buildAcceptErrorMessage(accept) {
  const extensions = getAllowedExtensions(accept);
  if (!extensions.length) return "This file type is not allowed.";
  return `Allowed file types: ${extensions.join(", ")}.`;
}

function resolveSampleHref(sampleHref) {
  if (!sampleHref) return "";
  if (typeof sampleHref === "string") return sampleHref;

  if (typeof sampleHref === "object") {
    return (
      sampleHref.Payload ||
      sampleHref.TemplateURL ||
      sampleHref.TemplateUrl ||
      sampleHref.URL ||
      sampleHref.Url ||
      sampleHref.href ||
      sampleHref.data ||
      ""
    );
  }

  return "";
}

export default function FileUpload({
  label = "Upload File",
  title = "Choose a file",
  description = "",
  accept = "",
  maxFileSize,
  selectedFile = null,
  selectedFileName = "",
  error = "",
  disabled = false,
  loading = false,
  compact = false,
  downloadSampleLabel = "",
  sampleHref = "",
  onFileChange,
  onDownloadSample,
  onValidationError,
}) {
  const inputRef = useRef(null);
  const [internalError, setInternalError] = useState("");
  const resolvedSampleHref = resolveSampleHref(sampleHref);

  const resolvedFileName = selectedFileName || selectedFile?.name || title;
  const resolvedDescription = useMemo(() => {
    if (selectedFile) {
      const sizeLabel = formatFileSize(selectedFile.size);
      return sizeLabel ? `${sizeLabel} selected` : description;
    }

    if (description) return description;

    if (maxFileSize) {
      return `Maximum file size ${formatFileSize(maxFileSize)}.`;
    }

    return "Select a file to continue.";
  }, [description, maxFileSize, selectedFile]);

  const activeError = error || internalError;

  useEffect(() => {
    if (!selectedFile && inputRef.current) {
      inputRef.current.value = "";
    }
  }, [selectedFile]);

  const handleClick = () => {
    if (disabled || loading) return;
    setInternalError("");
    onValidationError?.("");
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const handleChange = (event) => {
    const file = event.target.files?.[0] || null;
    setInternalError("");
    onValidationError?.("");

    if (!file) {
      onFileChange?.(null);
      return;
    }

    if (!matchesAccept(file, accept)) {
      const message = buildAcceptErrorMessage(accept);
      setInternalError(message);
      onValidationError?.(message);
      onFileChange?.(null);
      return;
    }

    if (maxFileSize && file.size > maxFileSize) {
      const message = `File should not be larger than ${formatFileSize(maxFileSize)}.`;
      setInternalError(message);
      onValidationError?.(message);
      onFileChange?.(null);
      return;
    }

    onFileChange?.(file);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setInternalError("");
    onValidationError?.("");
    onFileChange?.(null);
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const sampleControl =
    downloadSampleLabel ? (
      resolvedSampleHref ? (
        <a
          className="file-upload__sample-link"
          href={resolvedSampleHref}
          target="_blank"
          download
          rel="noreferrer"
        >
          <FontAwesomeIcon icon={faFileArrowDown} />
          <span>{downloadSampleLabel}</span>
        </a>
      ) : onDownloadSample ? (
        <button
          type="button"
          className="file-upload__sample-link"
          onClick={onDownloadSample}
          disabled={disabled || loading}
        >
          <FontAwesomeIcon icon={faFileArrowDown} />
          <span>{downloadSampleLabel}</span>
        </button>
      ) : (
        <button
          type="button"
          className="file-upload__sample-link is-disabled"
          disabled
        >
          <FontAwesomeIcon icon={faFileArrowDown} />
          <span>{downloadSampleLabel}</span>
        </button>
      )
    ) : null;

  return (
    <div className={`file-upload ${disabled ? "is-disabled" : ""}`}>
      {label || sampleControl ? (
        <div className="file-upload__label-row">
          {label ? <label className="file-upload__label">{label}</label> : <span />}
          {sampleControl}
        </div>
      ) : null}

      <label
        className={`file-upload__dropzone ${activeError ? "has-error" : ""} ${
          loading ? "is-loading" : ""
        } ${compact ? "is-compact" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled || loading}
          onClick={handleClick}
          onChange={handleChange}
        />
        <span className="file-upload__dropzone-icon" aria-hidden="true">
          <FontAwesomeIcon icon={loading ? faSpinner : faCloudArrowUp} spin={loading} />
        </span>
        <span className="file-upload__dropzone-copy">
          <strong>{resolvedFileName}</strong>
          <small>{resolvedDescription}</small>
        </span>
        {selectedFile && !disabled && !loading ? (
          <button
            type="button"
            className="file-upload__clear"
            onClick={handleClear}
            aria-label="Remove uploaded file"
            title="Remove file"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        ) : null}
      </label>

      {activeError ? <p className="file-upload__error">{activeError}</p> : null}
    </div>
  );
}
